import { create } from 'zustand';
import { authAPI } from '@/lib/api';
import { tokenStore } from '@/lib/api/baseAPI';
import { config } from '@/config';
import type { User } from '@/types/user';
import type { RegisterRequest } from '@/lib/api/auth';

interface AuthState {
  user: User | null;
  isInitialized: boolean;
  isLoading: boolean;
  error: string | null;
  isAuthenticated: () => boolean;
  init: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  signup: (payload: RegisterRequest) => Promise<void>;
  logout: () => Promise<void>;
  setError: (msg: string | null) => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isInitialized: false,
  isLoading: false,
  error: null,

  isAuthenticated: () => !!tokenStore.getAccess() && !!get().user,

  init: async () => {
    if (!tokenStore.getAccess()) {
      set({ isInitialized: true });
      return;
    }
    try {
      const user = await authAPI.me();
      set({ user, isInitialized: true });
    } catch {
      tokenStore.clear();
      set({ user: null, isInitialized: true });
    }
  },

  login: async (email, password) => {
    set({ isLoading: true, error: null });
    try {
      const res = await authAPI.login({ email, password });
      set({ user: res.user, isLoading: false });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Login failed';
      set({ error: msg, isLoading: false });
      throw err;
    }
  },

  signup: async (payload) => {
    set({ isLoading: true, error: null });
    try {
      const res = await authAPI.register(payload);
      // Backend returns the same AuthResponse as /login + already issues
      // tokens. Drop the user straight into an authenticated session.
      set({ user: res.user, isLoading: false });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Sign-up failed';
      set({ error: msg, isLoading: false });
      throw err;
    }
  },

  logout: async () => {
    // authAPI.logout is bulletproof (never throws, always clears tokens) so
    // we don't need a try/catch here. But we DO need to clear user state
    // explicitly — clearing tokens alone doesn't trigger an AuthGate
    // re-render. error is cleared too so a stale auth error from a previous
    // failed login doesn't persist across the new session.
    await authAPI.logout();
    set({ user: null, error: null, isLoading: false });
  },

  setError: (msg) => set({ error: msg }),
}));

if (typeof window !== 'undefined') {
  // (1) Refresh-interceptor failures dispatch this on the current tab so the
  //     store can drop user state immediately — no UI hang while a 401
  //     bounces around the page.
  window.addEventListener('auth:logout', () => {
    useAuthStore.setState({ user: null });
  });

  // (2) Cross-tab logout: if any other tab clears the access token (via
  //     tokenStore.clear), this `storage` event fires here. We drop user
  //     state so AuthGate redirects to /login on the next render. Tab-local
  //     logouts also trigger this (no harm — set is idempotent).
  window.addEventListener('storage', (e) => {
    if (
      e.key === config.storage.accessTokenKey &&
      e.newValue === null
    ) {
      useAuthStore.setState({ user: null });
    }
  });
}
